import type { PushConfigDto } from '@dijitalapp/shared';
import { api, ApiError } from './api';

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on';

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function keyToBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' });
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const config = await api<PushConfigDto>('GET', '/push/config');
  if (!config.available) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  return config.subscribed && Notification.permission === 'granted' ? 'on' : 'off';
}

/** Must be called from a user gesture: browsers only show the permission prompt then. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const config = await api<PushConfigDto>('GET', '/push/config');
  if (!config.available || !config.vapidPublicKey) return 'unavailable';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const reg = await registration();
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyToBytes(config.vapidPublicKey) }));
  const json = sub.toJSON();
  try {
    await api('POST', '/push/subscriptions', { endpoint: json.endpoint, keys: json.keys });
  } catch (err) {
    // The server refused this push service (unknown vendor): do not leave a dangling subscription.
    if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') await sub.unsubscribe();
    throw err;
  }
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  if (pushSupported()) {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe();
  }
  await api('DELETE', '/push/subscriptions');
  return 'off';
}
